'use client'

import { useCallback, useSyncExternalStore } from 'react'

const getFalseServerSnapshot = () => false
const getTrueServerSnapshot = () => true

export interface UseMediaQueryOptions {
  /**
   * Value rendered on the server and during hydration, when `matchMedia` is
   * not available. Defaults to `false`. Pass a request-derived guess (e.g. from
   * `sec-ch-ua-mobile`) so the SSR HTML first-paints the right branch.
   */
  serverSnapshot?: boolean
}

/**
 * Hook that returns whether the given media query matches.
 *
 * Hydration-safe: React keeps `serverSnapshot` for the hydrating render before
 * re-rendering with the live `matchMedia` result, so server and first client
 * markup always agree. Anything that paints in SSR HTML therefore first-paints
 * the `serverSnapshot` branch — use CSS variants there when no hint is known.
 */
export function useMediaQuery(
  query: string,
  { serverSnapshot = false }: UseMediaQueryOptions = {}
): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const mediaQuery = window.matchMedia(query)
      mediaQuery.addEventListener('change', onStoreChange)
      return () => mediaQuery.removeEventListener('change', onStoreChange)
    },
    [query]
  )
  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query])

  return useSyncExternalStore(
    subscribe,
    getSnapshot,
    serverSnapshot ? getTrueServerSnapshot : getFalseServerSnapshot
  )
}

/**
 * Hook that returns whether the viewport is below a given breakpoint.
 */
export function useIsBelowBreakpoint(
  breakpointPx: number,
  options?: UseMediaQueryOptions
): boolean {
  return useMediaQuery(`(max-width: ${breakpointPx - 1}px)`, options)
}
